import { ref, computed } from 'vue';
import store from '@/store';
import { t } from '@/utils/i18n-helper';

/**
 * Composable para gestionar los pasos del checkout SGA y el estado del proceso de compra
 * Sigue el Playbook v1.0: Toda lógica de estado >10 líneas vive en composables, no en componentes
 * 
 * Exporta:
 * - steps: Array computado de pasos basado en tipo_tpv del teatro
 * - Refs de estado: zonaSeleccionada, itemsCarrito, datosComprador, etc.
 * - Handlers para mutar ese estado: onSesionCreada, setDatosCobro, etc.
 */
export function useSgaCheckoutSteps() {
    // ── ESTADO REACTIVO ──────────────────────────────────────────────────────
    const zonaSeleccionada = ref(null);
    const itemsCarrito = ref(null);
    const sessionId = ref(null);
    const formulario = ref(null);
    const jetId = ref(null);
    const cartId = ref(null);
    const datosComprador = ref({
        nombre: '',
        apellidos: '',
        edad: '',
        telefono: null,
        cp: '',
        tipo_dni: '',
        dni: '',
        email: '',
        email_confirmation: '',
        dispositivo: null
    });

    // ── PROPIEDADES COMPUTADAS ───────────────────────────────────────────────
    /**
     * Define los pasos del stepper basado en la configuración del teatro.
     * Si tipo_tpv === 2, incluye paso de pago.
     */
    const steps = computed(() => {
        const baseSteps = [
            { label: t('front.sga.checkout.paso_1_label'), description: 'front.sga.checkout.paso_1_descripcion', slot: 'pasoPerfil' },
            { label: t('front.sga.checkout.paso_2_label'), description: 'front.sga.checkout.paso_2_descripcion', slot: 'pasoEntitlement' },
            { label: t('front.sga.checkout.paso_3_label'), description: 'front.sga.checkout.paso_3_descripcion', slot: 'pasoSector' },
            { label: t('front.sga.checkout.paso_4_label'), description: 'front.sga.checkout.paso_4_descripcion', slot: 'pasoAsiento' },
            { label: t('front.sga.checkout.paso_5_label'), description: 'front.sga.checkout.paso_5_descripcion', slot: 'pasoDatos' },
            { label: t('front.sga.checkout.paso_6_label'), description: 'front.sga.checkout.paso_6_descripcion', slot: 'pasoResumen' },
        ];

        const teatro = store.getters.getTeatro;
        if (teatro?.tipo_tpv === 2) {
            baseSteps.push({ label: t('front.sga.checkout.paso_7_label'), description: 'front.sga.checkout.paso_7_descripcion', slot: 'pasoTarjeta' });
        }

        return baseSteps;
    });

    // ── HANDLERS / MUTADORES ─────────────────────────────────────────────────

    /**
     * Llamado cuando se ha creado una sesión en SeleccionarAsientosSga
     * Establece el carrito de items y el ID del carrito
     */
    function onSesionCreada(items, carritoId) {
        itemsCarrito.value = items;
        cartId.value = carritoId;
    }

    /**
     * Establece datos de cobro (JET, formulario, sesión, carrito)
     * Típicamente llamado desde DatosCompradoresSgaComponent
     */
    function setDatosCobro(newJetId, newFormulario, newSessionId, newCartId) {
        jetId.value = newJetId;
        formulario.value = newFormulario;
        sessionId.value = newSessionId;
        cartId.value = newCartId;
    }

    /**
     * Actualiza datos del comprador y propaga a los items del carrito
     * Típicamente llamado desde DatosCompradoresSgaComponent después de rellenar datos
     */
    function updateDatosCompradores(datos, entradas) {
        datosComprador.value = { ...datos };
        if (itemsCarrito.value) {
            itemsCarrito.value = itemsCarrito.value.map((item, idx) => ({
                ...item,
                ...entradas[idx]
            }));
        }
    }

    /**
     * Establece la zona seleccionada por el usuario
     * Playbook v1.0 compliance: Template NUNCA muta refs directamente
     */
    function setZonaSeleccionada(zona) {
        zonaSeleccionada.value = zona;
    }

    /**
     * Limpia la zona seleccionada (cuando el usuario retrocede)
     * Playbook v1.0 compliance: Template NUNCA muta refs directamente
     */
    function clearZonaSeleccionada() {
        zonaSeleccionada.value = null;
    }

    /**
     * Limpia todos los datos del checkout (reinicia el proceso)
     */
    function eliminarDatos() {
        zonaSeleccionada.value = null;
        itemsCarrito.value = null;
        sessionId.value = null;
        formulario.value = null;
        jetId.value = null;
        cartId.value = null;
        datosComprador.value = {
            nombre: '',
            apellidos: '',
            edad: '',
            telefono: null,
            cp: '',
            tipo_dni: '',
            dni: '',
            email: '',
            email_confirmation: '',
            dispositivo: null
        };
    }

    return {
        // Estado
        zonaSeleccionada,
        itemsCarrito,
        sessionId,
        formulario,
        jetId,
        cartId,
        datosComprador,
        // Computed
        steps,
        // Handlers
        setZonaSeleccionada,
        clearZonaSeleccionada,
        onSesionCreada,
        setDatosCobro,
        updateDatosCompradores,
        eliminarDatos
    };
}
